import React, { useEffect, useMemo, Component } from 'react';
/**
 * SessionSummary — End-of-session results screen with mastery growth.
 *
 * DDD co-locatie: moved from components/practice/ in s65.
 * Optimized: confetti data, types, copy helpers, useCountUp → sessionSummaryData.ts (~80r).
 */
import { motion } from 'framer-motion';
import { Star, RotateCcw, CheckCircle2, Sparkles, X, Zap, Clock, TrendingUp } from 'lucide-react';
import { type ModuleId, type Question, getFamilyDefinition } from '../../content';
import type { FamilyMasteryState } from '../../mastery';
import { getFamilyMasteryKey, getGrowthStage } from '../../mastery';
import { GrowthStageIcon } from '../../components/shared/GrowthStageIcon';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useAppStore } from '../../app/hooks/useAppStore';
import { dataLayer, type PersonalRecords } from '../../data/dataLayer';
import { soundManager } from '../../utils/soundManager';
import { DjayAvatar, type DjayEmotion, useDjaySelection, getDjaySummaryReaction } from '../../mascot';
import { SESSION_CONFIG } from '../../config/constants';
import { type SessionResult, CONFETTI, getSummaryHeading, useCountUp } from './sessionSummaryData';
// Re-export for consumers that import the type from here
export type { SessionResult };
// ── Props ────────────────────────────────────────────────────
interface SessionSummaryProps {
  moduleId: ModuleId;
  sessionQuestions: Question[];
  sessionAnswers: SessionResult[];
  preFamilyMastery: Record<string, FamilyMasteryState>;
  isFluency?: boolean;
  labMode?: string;
  onExit: () => void;
  onPracticeAgain?: () => void;
  xpGained: number;
}
// ── Component ────────────────────────────────────────────────
export function SessionSummary({
  moduleId,
  sessionQuestions,
  sessionAnswers,
  preFamilyMastery,
  isFluency = false,
  labMode,
  onExit,
  onPracticeAgain,
  xpGained
}: SessionSummaryProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const currentFamilyMastery = state.familyMastery || {};
  const correctCount = sessionAnswers.filter(a => a.isCorrect).length;
  const totalQuestions = sessionAnswers.length;
  const percentage = totalQuestions > 0 ? Math.round(correctCount / totalQuestions * 100) : 0;
  const isGreat = percentage >= 80;
  // Calculate average speed
  const averageSpeedMs = useMemo(() => {
    if (sessionAnswers.length === 0) return 0;
    const total = sessionAnswers.reduce((sum, a) => sum + (a.timeMs || 0), 0);
    return total / sessionAnswers.length;
  }, [sessionAnswers]);
  const averageSpeedSec = (averageSpeedMs / 1000).toFixed(1);
  // Calculate fluency stats
  const fluencyStats = useMemo(() => {
    if (!isFluency || sessionAnswers.length === 0) return null;
    // Fastest correct answer
    const correctAnswers = sessionAnswers.filter(a => a.isCorrect && a.timeMs);
    let fastestAnswer = null;
    if (correctAnswers.length > 0) {
      fastestAnswer = correctAnswers.reduce((min, curr) => curr.timeMs! < min.timeMs! ? curr : min);
    }
    // Longest streak
    let currentStreak = 0;
    let longestStreak = 0;
    sessionAnswers.forEach(a => {
      if (a.isCorrect) {
        currentStreak++;
        if (currentStreak > longestStreak) longestStreak = currentStreak;
      } else {
        currentStreak = 0;
      }
    });
    return {
      fastestAnswer,
      longestStreak
    };
  }, [isFluency, sessionAnswers]);
  // Djay emotion + speech from registry (single source of truth)
  const summaryReaction = useMemo(() => {
    if (isFluency && averageSpeedMs > 0) {
      const isFast = averageSpeedMs < SESSION_CONFIG.FLUENCY_FAST_THRESHOLD_MS;
      const isMedium = averageSpeedMs < SESSION_CONFIG.FLUENCY_SLOW_THRESHOLD_MS;
      return {
        emotion: isFast ? 'celebrating' : isMedium ? 'happy' : 'neutral',
        text: isFast ? 'Wat ben jij razendsnel!' : isMedium ? 'Lekker vlot gewerkt!' : 'Goed gefocust!'
      } as {
        emotion: DjayEmotion;
        text: string;
      };
    }
    return getDjaySummaryReaction(percentage);
  }, [percentage, isFluency, averageSpeedMs]);
  const djayEmotion: DjayEmotion = summaryReaction.emotion;
  // Animated score counter
  const animatedCorrect = useCountUp(correctCount, 1.2, 0.5);
  // Heading copy
  const baseHeading = getSummaryHeading(percentage);
  const heading = isFluency ? 'Snel & Zeker' : baseHeading.heading;
  const subtitle = isFluency ? 'Je bent weer een stukje sneller geworden!' : baseHeading.subtitle;
  // Practiced families (max 4)
  const practicedFamilyIds = Array.from(new Set(sessionAnswers.map(q => q.familyId).filter((id): id is string => !!id))).slice(0, 4);
  useEffect(() => {
    if (labMode) {
      dataLayer.saveLastOefenlabSession({
        modeId: labMode,
        familyId: practicedFamilyIds[0],
        timestamp: Date.now(),
        score: percentage
      });
      // Check for newly unlocked challenges
      for (const familyId of practicedFamilyIds) {
        const masteryKey = getFamilyMasteryKey(familyId, selectedGroup);
        const pre = preFamilyMastery[masteryKey]?.masteryScore || 0;
        const post = currentFamilyMastery[masteryKey]?.masteryScore || 0;
        if (pre < 70 && post >= 70) {
          dataLayer.setNewlyUnlockedChallenge(familyId);
          break; // Just store one for now to avoid multiple notifications
        }
      }
    }
  }, [labMode, practicedFamilyIds, percentage, preFamilyMastery, currentFamilyMastery, selectedGroup]);
  // F-9: Update personal records
  useEffect(() => {
    if (!labMode) return;
    dataLayer.getPersonalRecords().then(records => {
      const newRecords: PersonalRecords = {
        ...records
      };
      if (labMode === 'fluency') {
        const currentFluency = newRecords.fluency || {
          fastestAvgMs: Infinity,
          longestStreak: 0,
          bestScore: 0
        };
        newRecords.fluency = {
          fastestAvgMs: averageSpeedMs > 0 ? Math.min(currentFluency.fastestAvgMs, averageSpeedMs) : currentFluency.fastestAvgMs,
          longestStreak: Math.max(currentFluency.longestStreak, fluencyStats?.longestStreak || 0),
          bestScore: Math.max(currentFluency.bestScore, percentage)
        };
      } else if (labMode === 'sprint') {
        const currentSprint = newRecords.sprint || {
          bestScore: 0,
          totalSessions: 0
        };
        newRecords.sprint = {
          bestScore: Math.max(currentSprint.bestScore, percentage),
          totalSessions: currentSprint.totalSessions + 1
        };
      } else if (labMode === 'challenge') {
        const currentChallenge = newRecords.challenge || {
          highestTier: 0,
          bestScore: 0,
          totalCompleted: 0
        };
        // We don't have tier info easily available here, so we just update score and count
        newRecords.challenge = {
          ...currentChallenge,
          bestScore: Math.max(currentChallenge.bestScore, percentage),
          totalCompleted: currentChallenge.totalCompleted + 1
        };
      }
      dataLayer.savePersonalRecords(newRecords);
    }).catch(() => {});
  }, [labMode, averageSpeedMs, fluencyStats, percentage]);
  // Sound feedback on summary mount
  useEffect(() => {
    if (percentage >= 80) {
      soundManager.playCorrect();
    } else {
      soundManager.playNavigate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className={`min-h-screen ${isFluency ? 'bg-amber-50' : 'bg-slate-50'} p-4 md:p-8 flex items-center justify-center`} data-id="element-8837">
      <div className="max-w-lg w-full" data-id="element-8838">
        <Card className={`p-6 md:p-8 shadow-2xl ${isFluency ? 'shadow-amber-100/60 border-amber-100' : 'shadow-indigo-100/60 border-slate-100'} bg-white rounded-3xl relative overflow-hidden`} data-id="element-8839">
          {/* X button — top right */}
          <button onClick={onExit} className="absolute top-4 right-4 z-30 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Sluiten" data-id="element-8840">
            <X className="w-5 h-5" data-id="element-8841" />
          </button>

          {/* Confetti particles for high scores */}
          {isGreat && CONFETTI.map((dot, i) => <motion.div key={i} className={`absolute rounded-full ${dot.color} ${dot.size} pointer-events-none`} style={{
          top: dot.top,
          left: dot.left,
          right: dot.right
        }} initial={{
          opacity: 0,
          scale: 0,
          y: 0
        }} animate={{
          opacity: [0, 1, 1, 0],
          scale: [0, 1.3, 1, 0.7],
          y: [0, -12, -20, -30]
        }} transition={{
          delay: dot.delay,
          duration: 2.5,
          ease: 'easeOut'
        }} data-id="element-8842" />)}

          {/* ── COMPACT HEADER ── */}
          <div className="text-center mb-6 relative z-10 pt-2" data-id="element-8843">
            {/* @djay-slot: summary-header */}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            bounce: 0.5,
            delay: 0.1
          }} className="flex items-center justify-center mx-auto mb-4" data-id="element-8844">
              <DjayAvatar size="xl" emotion={djayEmotion} variant={selectedVariant} data-id="element-8845" />
            </motion.div>

            {/* Djay speech bubble */}
            <motion.div initial={{
            opacity: 0,
            y: 6,
            scale: 0.92
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            delay: 0.25,
            duration: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative mb-4" data-id="element-8846">
              <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-t border-l border-slate-200/70 z-10" data-id="element-8847" />
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-md shadow-slate-200/50 border border-slate-200/70 px-5 py-2.5 text-center max-w-[280px] mx-auto" data-id="element-8848">
                <p className="font-bold text-slate-700 text-sm leading-snug" data-id="element-8849">
                  {summaryReaction.text}
                </p>
              </div>
            </motion.div>

            {/* Heading */}
            <motion.h2 initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className={`text-3xl md:text-4xl font-black ${isFluency ? 'text-amber-900' : 'text-slate-900'} mb-2 tracking-tight`} data-id="element-8850">
              {heading}
            </motion.h2>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} className={`text-base md:text-lg font-medium ${isFluency ? 'text-amber-700/80' : 'text-slate-500'} mb-4`} data-id="element-8851">
              {subtitle}
            </motion.p>

            {/* Score — clean inline stats */}
            <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5,
            duration: 0.4
          }} className="flex items-center justify-center gap-2 flex-wrap" data-id="element-8852">
              {isFluency && averageSpeedMs > 0 ? <>
                  <div className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-200" data-id="element-8853">
                    <Zap className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" data-id="element-8854" />
                    <span className="font-bold text-amber-800 text-sm" data-id="element-8855">
                      {averageSpeedSec}s gemiddeld
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-full border border-slate-100" data-id="element-8856">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-8857" />
                    <span className="font-bold text-slate-700 text-sm" data-id="element-8858">
                      <motion.span data-id="element-8859">{animatedCorrect}</motion.span>/
                      {totalQuestions} goed
                    </span>
                  </div>
                </> : <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-full border border-slate-100" data-id="element-8860">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-8861" />
                  <span className="font-bold text-slate-700 text-sm" data-id="element-8862">
                    <motion.span data-id="element-8863">{animatedCorrect}</motion.span> van de{' '}
                    {totalQuestions} goed
                  </span>
                </div>}
            </motion.div>
          </div>

          {/* ── MASTERY DELTA ── */}
          {!isFluency && practicedFamilyIds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-6" data-id="element-8864">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5 px-1" data-id="element-8865">
                <Sparkles className="w-4 h-4 text-indigo-400" data-id="element-8866" />
                Wat heb je geoefend?
              </h3>
              <div className="space-y-2" data-id="element-8867">
                {practicedFamilyIds.map((familyId, index) => {
              const def = getFamilyDefinition(familyId);
              const masteryKey = getFamilyMasteryKey(familyId, selectedGroup);
              const pre = preFamilyMastery[masteryKey]?.masteryScore || 0;
              const post = currentFamilyMastery[masteryKey]?.masteryScore || 0;
              const delta = post - pre;
              if (!def) return null;
              const preStage = getGrowthStage(pre);
              const postStage = getGrowthStage(post);
              // Real level up: only count transitions ABOVE level 1
              const leveledUp = postStage.level > preStage.level && preStage.level >= 1;
              return <motion.div key={familyId} initial={{
                opacity: 0,
                x: -8
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.7 + index * 0.08,
                duration: 0.35
              }} className="bg-slate-50 rounded-xl p-3 border border-slate-100 flex items-center gap-3" data-id="element-8868">
                      <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-white rounded-full border border-slate-200 shadow-sm" data-id="element-8869">
                        <GrowthStageIcon masteryScore={post} size="sm" data-id="element-8870" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-8871">
                        <div className="font-semibold text-slate-800 text-sm truncate" data-id="element-8872">
                          {def.description}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-8873">
                          {postStage.label}
                        </div>
                      </div>
                      <div className="flex-shrink-0" data-id="element-8874">
                        {leveledUp ? <motion.span initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    delay: 0.9 + index * 0.08
                  }} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100" data-id="element-8875">
                            <Sparkles className="w-3 h-3" data-id="element-8876" /> Level up!
                          </motion.span> : delta > 0 ? <span className="text-xs font-semibold text-indigo-500" data-id="element-8877">
                            {Math.round(pre)}% → {Math.round(post)}%
                          </span> : <CheckCircle2 className="w-4 h-4 text-slate-300" data-id="element-8878" />}
                      </div>
                    </motion.div>;
            })}
              </div>
            </motion.div>}

          {/* ── FLUENCY STATS ── */}
          {isFluency && fluencyStats && <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.75,
          duration: 0.5
        }} className="mb-6" data-id="element-8879">
              <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-3 flex items-center gap-1.5 px-1" data-id="element-8880">
                <Zap className="w-4 h-4" data-id="element-8881" />
                Snel & Zeker Resultaten
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-8882">
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100" data-id="element-8883">
                  <div className="flex items-center gap-2 text-amber-800 mb-1" data-id="element-8884">
                    <TrendingUp className="w-4 h-4" data-id="element-8885" />
                    <span className="text-xs font-bold uppercase" data-id="element-8886">
                      Langste Reeks
                    </span>
                  </div>
                  <div className="text-2xl font-black text-amber-600" data-id="element-8887">
                    {fluencyStats.longestStreak}{' '}
                    <span className="text-sm font-medium text-amber-700/70" data-id="element-8888">
                      goed
                    </span>
                  </div>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100" data-id="element-8889">
                  <div className="flex items-center gap-2 text-amber-800 mb-1" data-id="element-8890">
                    <Clock className="w-4 h-4" data-id="element-8891" />
                    <span className="text-xs font-bold uppercase" data-id="element-8892">Snelste</span>
                  </div>
                  <div className="text-2xl font-black text-amber-600" data-id="element-8893">
                    {fluencyStats.fastestAnswer ? (fluencyStats.fastestAnswer.timeMs! / 1000).toFixed(1) : '-'}{' '}
                    <span className="text-sm font-medium text-amber-700/70" data-id="element-8894">
                      sec
                    </span>
                  </div>
                </div>
              </div>

              {/* Per-question speed breakdown */}
              <div className="bg-white border border-amber-100 rounded-xl overflow-hidden" data-id="element-8895">
                <div className="bg-amber-50/50 px-3 py-2 border-b border-amber-100 text-xs font-bold text-amber-800" data-id="element-8896">
                  Snelheid per vraag
                </div>
                <div className="max-h-32 overflow-y-auto p-1" data-id="element-8897">
                  {sessionAnswers.map((ans, i) => {
                const q = sessionQuestions.find(q => q.id === ans.questionId);
                const qText = q?.question ? q.question.length > 30 ? q.question.substring(0, 30) + '...' : q.question : `Vraag ${i + 1}`;
                return <div key={i} className="flex items-center justify-between px-2 py-1.5 hover:bg-amber-50/30 rounded-lg text-sm" data-id="element-8898">
                        <div className="flex items-center gap-2 truncate pr-2" data-id="element-8899">
                          {ans.isCorrect ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-8900" /> : <X className="w-3.5 h-3.5 text-rose-400 flex-shrink-0" data-id="element-8901" />}
                          <span className="text-slate-600 truncate" data-id="element-8902">
                            {qText}
                          </span>
                        </div>
                        <div className={`font-mono font-medium flex-shrink-0 ${ans.timeMs && ans.timeMs <= 8000 ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-8903">
                          {ans.timeMs ? (ans.timeMs / 1000).toFixed(1) + 's' : '-'}
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </motion.div>}

          {/* ── ENCOURAGEMENT ── */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.85,
          duration: 0.4
        }} className="mb-6" data-id="element-8904">
            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 text-center" data-id="element-8905">
              <div className="flex items-center justify-center gap-4 mb-3" data-id="element-8906">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-indigo-50" data-id="element-8907">
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" data-id="element-8908" />
                  <span className="font-black text-indigo-900" data-id="element-8909">
                    +{xpGained} XP
                  </span>
                  <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider" data-id="element-8910">
                    verdiend
                  </span>
                </div>
              </div>
              <div className="font-bold text-indigo-900 text-base" data-id="element-8911">
                {percentage >= 80 ? 'Wauw, wat een prestatie! 🌟' : percentage >= 50 ? 'Goed bezig! Je bent op de goede weg. 💪' : 'Oefening baart kunst! Je hebt nu gezien waar je aan kunt werken. 🌱'}
              </div>
              <div className="text-sm text-indigo-600/80 mt-1" data-id="element-8912">
                {percentage >= 80 ? 'Je hebt dit onderwerp goed in de vingers.' : percentage >= 50 ? 'Elke keer word je een beetje beter.' : 'Fouten maken is de beste manier om te leren.'}
              </div>

              <div className="mt-5 pt-4 border-t border-indigo-100/50 flex items-start gap-3 text-left" data-id="element-8913">
                <div className="flex-shrink-0 mt-0.5" data-id="element-8914">
                  <DjayAvatar size="sm" emotion={percentage >= 80 ? 'proud' : 'pointing'} variant={selectedVariant} data-id="element-8915" />
                </div>
                <div data-id="element-8916">
                  <div className="text-[10px] font-black text-indigo-400 uppercase tracking-wider mb-0.5" data-id="element-8917">
                    Djay's Tip
                  </div>
                  <div className="text-sm font-medium text-indigo-800 leading-snug" data-id="element-8918">
                    {percentage >= 80 ? 'Je hebt de stof super goed begrepen. Probeer de volgende keer een Snel & Zeker sessie om je snelheid te trainen!' : percentage >= 50 ? 'Kijk nog even goed naar de vragen die je fout had. Van je fouten leer je het meest!' : 'Neem de tijd voor elke vraag. Het is helemaal niet erg om de theorie erbij te pakken!'}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── ACTION BUTTONS ── */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.0,
          duration: 0.4
        }} className="relative z-20 space-y-2.5" data-id="element-8919">
            {onPracticeAgain && <Button onClick={onPracticeAgain} size="xl" variant="primary" fullWidth className="shadow-lg shadow-indigo-200/50 h-14 text-base rounded-2xl" data-id="element-8920">
                <RotateCcw className="w-5 h-5 mr-2" data-id="element-8921" />
                Nog een keer oefenen
              </Button>}
            <Button onClick={onExit} size="xl" variant={onPracticeAgain ? 'secondary' : 'primary'} fullWidth className={`h-14 text-base rounded-2xl ${onPracticeAgain ? '' : 'shadow-lg shadow-indigo-200/50'}`} data-id="element-8922">
              {onPracticeAgain ? 'Probeer een andere modus' : 'Terug naar Oefenlab'}
            </Button>
          </motion.div>
        </Card>
      </div>
    </div>;
}